/**
 * FILE: packages/supabase/src/students/reads.ts
 * PURPOSE: Student read operations.
 * AGENT_CONTEXT: All reads use .is('deleted_at', null).
 */

import type { DbClient } from '../dbClient'
import { defaultDbClient } from '../dbClient'
import { today } from '@fintree/utils'
import type { PostgrestFilterBuilder } from '@supabase/supabase-js'
import type { Database, Json } from '../types'
import type { EmailConflictInfo } from '../internal/errors'
import type {
  CityFilterOption,
  DuplicateStudentGroup,
  ListStudentsResult,
  StudentCityFilterCountsResult,
  StudentDuplicateResult,
  StudentListItem,
  StudentSearchResult,
  StudentsCityCountFilters,
  StudentsFilters,
  StudentsSortDirection,
  StudentsSortField,
  ValidTillFilter,
} from './dto'

type StudentRow = Database['public']['Tables']['students']['Row']
type ClientOptions = Database extends { __InternalSupabase: infer O } ? O : never
type StudentsQuery = PostgrestFilterBuilder<
  ClientOptions,
  Database['public'],
  StudentRow,
  StudentRow[],
  'students',
  unknown,
  'GET'
>

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function toDate(dateString: string): Date {
  return new Date(`${dateString}T00:00:00`)
}

export function buildValidTillConditions(filters: ValidTillFilter[]): string[] {
  if (!filters.length) return []

  const currentDay = today()
  const todayDate = toDate(currentDay)
  const next30Days = formatIsoDate(addDays(todayDate, 30))
  const next60Days = formatIsoDate(addDays(todayDate, 60))
  const next90Days = formatIsoDate(addDays(todayDate, 90))

  const conditions: string[] = []
  if (filters.includes('expired')) conditions.push(`valid_till.lt.${currentDay}`)
  if (filters.includes('expiring') || filters.includes('expiring_30')) {
    conditions.push(`and(valid_till.gte.${currentDay},valid_till.lte.${next30Days})`)
  }
  if (filters.includes('expiring_60')) conditions.push(`and(valid_till.gte.${currentDay},valid_till.lte.${next60Days})`)
  if (filters.includes('expiring_90')) conditions.push(`and(valid_till.gte.${currentDay},valid_till.lte.${next90Days})`)
  if (filters.includes('empty')) conditions.push('valid_till.is.null')
  return conditions
}

function formatPostgrestList(values: string[]): string {
  return values.map((value) => `"${value.replaceAll('"', '\\"')}"`).join(',')
}

export function buildExpBandConditions(filters: string[]): string[] {
  if (!filters.length) return []

  const includeNotSet = filters.includes('not_set')
  const selectedBands = filters.filter((band) => band !== 'not_set')

  if (!includeNotSet) {
    return selectedBands.length ? [`exp_band.in.(${formatPostgrestList(selectedBands)})`] : []
  }

  const conditions = ['exp_band.is.null', 'exp_band.eq.""']
  if (selectedBands.length) {
    conditions.push(`exp_band.in.(${formatPostgrestList(selectedBands)})`)
  }
  return conditions
}

export function buildCityConditions(filters: string[]): string[] {
  if (!filters.length) return []

  const includeNotSet = filters.includes('not_set')
  const selectedCities = filters.filter((city) => city !== 'not_set')

  if (!includeNotSet) {
    return selectedCities.length ? [`city.in.(${formatPostgrestList(selectedCities)})`] : []
  }

  const conditions = ['city.is.null', 'city.eq.""']
  if (selectedCities.length) {
    conditions.push(`city.in.(${formatPostgrestList(selectedCities)})`)
  }
  return conditions
}

export function toCityCountFilterPayload(filters: StudentsCityCountFilters): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  const search = filters.search?.trim()
  if (search) payload.search = search
  if (filters.courses?.length) payload.courses = filters.courses
  if (filters.packages?.length) payload.packages = filters.packages
  if (filters.expBands?.length) payload.expBands = filters.expBands
  if (filters.statuses?.length) payload.statuses = filters.statuses
  if (filters.validTill?.length) payload.validTill = filters.validTill
  if (filters.missingCourse) payload.missingCourse = true
  if (filters.missingName) payload.missingName = true
  if (filters.missingDate) payload.missingDate = true
  if (filters.anyIncomplete) payload.anyIncomplete = true
  if (filters.needsReview) payload.needsReview = true
  if (filters.showDeleted) payload.showDeleted = true
  return payload
}

function applyStudentsFilters(query: StudentsQuery, filters: StudentsFilters): StudentsQuery {
  let next = filters.showDeleted ? query.not('deleted_at', 'is', null) : query.is('deleted_at', null)

  if (filters.search?.trim()) {
    const q = `%${filters.search.trim()}%`
    next = next.or(`name.ilike.${q},phone.ilike.${q}`)
  }
  if (filters.courses?.length) next = next.in('course', filters.courses)
  if (filters.packages?.length) next = next.in('program', filters.packages)
  if (filters.cities?.length) {
    const cityConditions = buildCityConditions(filters.cities)
    const firstCondition = cityConditions.at(0)
    if (cityConditions.length === 1 && firstCondition?.startsWith('city.in.(')) {
      const selectedCities = filters.cities.filter((city) => city !== 'not_set')
      if (selectedCities.length) {
        next = next.in('city', selectedCities)
      }
    } else if (cityConditions.length) {
      next = next.or(cityConditions.join(','))
    }
  }
  if (filters.expBands?.length) {
    const expBandConditions = buildExpBandConditions(filters.expBands)
    const firstCondition = expBandConditions.at(0)
    if (expBandConditions.length === 1 && firstCondition?.startsWith('exp_band.in.(')) {
      const selectedBands = filters.expBands.filter((band) => band !== 'not_set')
      if (selectedBands.length) {
        next = next.in('exp_band', selectedBands)
      }
    } else if (expBandConditions.length) {
      next = next.or(expBandConditions.join(','))
    }
  }
  if (filters.statuses?.length) next = next.in('status', filters.statuses)
  if (filters.missingCourse) next = next.is('course', null)
  if (filters.missingName) next = next.is('name', null)
  if (filters.missingDate) next = next.is('enrollment_date', null)
  if (filters.anyIncomplete) next = next.or('name.is.null,course.is.null,enrollment_date.is.null')
  if (filters.needsReview) next = next.eq('needs_review', true)
  if (filters.validTill?.length) {
    const validTillOrConditions = buildValidTillConditions(filters.validTill)
    if (validTillOrConditions.length) {
      next = next.or(validTillOrConditions.join(','))
    }
  }

  return next
}

function applyStudentsCursor(
  query: StudentsQuery,
  sortField: StudentsSortField,
  sortDirection: StudentsSortDirection,
  cursor: { value: string; phone: string } | null
): StudentsQuery {
  if (!cursor) return query
  const ascending = sortDirection === 'asc'
  const cmp = ascending ? 'gt' : 'lt'
  return query.or(`${sortField}.${cmp}.${cursor.value},and(${sortField}.eq.${cursor.value},phone.gt.${cursor.phone})`)
}

export async function getStudents(
  filters: StudentsFilters = {},
  page = 0,
  pageSize = 50,
  sortField: StudentsSortField = 'updated_at',
  sortDirection: StudentsSortDirection = 'desc',
  client: DbClient = defaultDbClient
): Promise<ListStudentsResult> {
  const safePage = Math.max(page ?? 0, 0)
  const safePageSize = pageSize ?? 50
  const from = safePage * safePageSize
  const to = from + safePageSize - 1

  const query = applyStudentsFilters(
    client
      .from('students')
      .select(
        'phone, name, email, course, program, attempt, valid_till, enrollment_date, exp_band, city, status, deleted_at, needs_review, updated_at, can_edit_course_in_portal',
        { count: 'exact', head: false }
      )
      .order(sortField, { ascending: sortDirection === 'asc', nullsFirst: false })
      .order('phone', { ascending: true }) as unknown as StudentsQuery,
    filters
  ).range(from, to)

  const { data, count, error } = await query
  if (error) throw error
  const result: StudentListItem[] = data ?? []
  return { data: result, count: count ?? 0 }
}

const STUDENTS_EXPORT_PAGE_SIZE = 500
const STUDENTS_EXPORT_MAX_PAGES = 500

/** Cursor-paginated export — avoids offset loops on 10k+ rows. */
export async function fetchAllStudentsForExport(
  filters: StudentsFilters = {},
  sortField: StudentsSortField = 'updated_at',
  sortDirection: StudentsSortDirection = 'desc',
  client: DbClient = defaultDbClient
): Promise<StudentRow[]> {
  const rows: StudentRow[] = []
  let cursor: { value: string; phone: string } | null = null
  let prevCursorKey: string | null = null

  for (let page = 0; page < STUDENTS_EXPORT_MAX_PAGES; page += 1) {
    const query = applyStudentsCursor(
      applyStudentsFilters(
        client
          .from('students')
          .select(
            'phone, name, email, course, program, attempt, valid_till, enrollment_date, exp_band, city, status, deleted_at, needs_review, updated_at, can_edit_course_in_portal'
          )
          .order(sortField, { ascending: sortDirection === 'asc', nullsFirst: false })
          .order('phone', { ascending: true })
          .limit(STUDENTS_EXPORT_PAGE_SIZE) as unknown as StudentsQuery,
        filters
      ),
      sortField,
      sortDirection,
      cursor
    )

    const { data, error } = await query
    if (error) throw error
    if (!data?.length) break

    rows.push(...data)
    if (data.length < STUDENTS_EXPORT_PAGE_SIZE) break

    const last = data[data.length - 1]
    const cursorValue = last?.[sortField]
    if (!last?.phone || cursorValue == null || cursorValue === '') break

    const nextCursorKey = `${String(cursorValue)}:${last.phone}`
    if (nextCursorKey === prevCursorKey) break
    prevCursorKey = nextCursorKey
    cursor = { value: String(cursorValue), phone: last.phone }
  }

  return rows
}

export async function listStudents(
  params: StudentsFilters & { page?: number; pageSize?: number; sortField?: StudentsSortField; sortDirection?: StudentsSortDirection } = {},
  client: DbClient = defaultDbClient
): Promise<ListStudentsResult> {
  return getStudents(
    params,
    params.page ?? 0,
    params.pageSize ?? 50,
    params.sortField ?? 'updated_at',
    params.sortDirection ?? 'desc',
    client
  )
}

export async function getStudentByPhone(
  phone: string,
  client: DbClient = defaultDbClient
): Promise<StudentRow | null> {
  const { data, error } = await client
    .from('students')
    .select(
      'phone, name, email, course, program, attempt, valid_till, enrollment_date, exp_band, city, status, deleted_at, deleted_by, needs_review, updated_at, created_at, first_name, country, country_code, current_company, designation, employment_status, graduation_field, graduation_status, post_graduation_field, post_graduation_status, cfa_status, fm_status, frm_status, amount, amount_paid, currency, gateway, is_installment, installment_count, extension_count, last_extended_at, onboarding_complete, placed_company, placed_date, placement_status, moodle_id, last_moodle_sync_at, sheet_synced, source, order_id, razorpay_payment_id, ccavenue_tracking_id, reconciliation_notes, sanity_id, template_sent, combo_package, combo_levels, certifications, has_juice_notes, pending_placement_refresh, can_edit_course_in_portal, id'
    )
    .eq('phone', phone)
    .is('deleted_at', null)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function getStudentCityOptionsForFilters(
  filters: StudentsCityCountFilters,
  client: DbClient = defaultDbClient
): Promise<StudentCityFilterCountsResult> {
  const { data, error } = await client.rpc('get_student_city_filter_counts', {
    p_filters: toCityCountFilterPayload(filters) as Json,
  })
  if (error) throw error

  const cities = (data as { cities?: { city: string; student_count: number }[]; not_set_count?: number } | null)?.cities ?? []
  const notSetCount = Number((data as { not_set_count?: number } | null)?.not_set_count ?? 0)

  return {
    options: cities.map((row) => ({
      city: row.city,
      studentCount: Number(row.student_count),
    })),
    notSetCount,
  }
}

export async function getStudentCityOptions(
  client: DbClient = defaultDbClient
): Promise<CityFilterOption[]> {
  const { data, error } = await client.rpc('get_student_city_options')
  if (error) throw error
  return (data ?? []).map((row: { city: string; student_count: number }) => ({
    city: row.city,
    studentCount: Number(row.student_count),
  }))
}

export async function getStudentCityNotSetCount(
  client: DbClient = defaultDbClient
): Promise<number> {
  const { data, error } = await client.rpc('get_student_city_not_set_count')
  if (error) throw error
  return Number(data ?? 0)
}

export async function searchStudentByPhone(
  phone: string,
  client: DbClient = defaultDbClient
): Promise<StudentDuplicateResult> {
  const student = await getStudentByPhone(phone, client)
  const [{ count, error: countError }, { data: activeEnrollments, error: listError }] = await Promise.all([
    client
      .from('enrollments')
      .select('id', { count: 'exact', head: true })
      .eq('phone', phone)
      .is('deleted_at', null),
    client
      .from('enrollments')
      .select('course, attempt, program')
      .eq('phone', phone)
      .is('deleted_at', null),
  ])

  if (countError) throw countError
  if (listError) throw listError

  return {
    student: student
      ? {
          phone: student.phone,
          name: student.name,
          course: student.course,
          program: student.program,
          attempt: student.attempt,
          valid_till: student.valid_till,
          status: student.status,
        }
      : null,
    enrollmentHistoryCount: count ?? 0,
    activeEnrollments: activeEnrollments ?? [],
  }
}

export async function getActiveStudentByEmail(
  email: string,
  excludePhone?: string,
  client: DbClient = defaultDbClient
): Promise<EmailConflictInfo | null> {
  const normalized = email.trim().toLowerCase()
  if (!normalized) return null
  let query = client
    .from('students')
    .select('phone, name, course')
    .eq('email', normalized)
    .is('deleted_at', null)
  if (excludePhone?.trim()) {
    query = query.neq('phone', excludePhone.trim())
  }
  const { data, error } = await query.maybeSingle()
  if (error) throw error
  return data
    ? { phone: data.phone, name: data.name, course: data.course }
    : null
}

export async function countStudents(client: DbClient = defaultDbClient): Promise<number> {
  const { count, error } = await client
    .from('students')
    .select('phone', { count: 'exact', head: true })
    .is('deleted_at', null)
  if (error) throw error
  return count ?? 0
}

export async function findDuplicateStudents(
  client: DbClient = defaultDbClient
): Promise<DuplicateStudentGroup[]> {
  const { data, error } = await client.rpc('find_duplicate_students')
  if (error) throw error
  const rawGroups = (data ?? []) as Array<{
    matched_on: 'email' | 'phone' | 'name'
    matched_value: string
    students: Array<{
      phone: string
      name: string | null
      email: string | null
      course: string | null
      program: string | null
      status: string | null
      valid_till: string | null
    }>
  }>
  return rawGroups.map((group) => ({
    matchedOn: group.matched_on,
    matchedValue: group.matched_value,
    students: group.students.map((s) => ({
      phone: s.phone,
      name: s.name,
      email: s.email,
      course: s.course,
      program: s.program,
      status: s.status,
      validTill: s.valid_till,
    })),
  }))
}

function normalizePhoneDigits(input: string): string {
  return input.replace(/\D/g, '')
}

function detectIdentifierType(input: string): 'email' | 'phone' | 'name' {
  if (input.includes('@')) return 'email'
  const digitsOnly = normalizePhoneDigits(input)
  if (digitsOnly.length > 0 && /^\+?\d+$/.test(input.replace(/\s/g, ''))) return 'phone'
  return 'name'
}

/**
 * Unified student search by email, phone, or name.
 * - Contains `@` → email ILIKE
 * - All digits or leading `+` followed by digits → phone ILIKE on normalized digits
 * - Otherwise → name ILIKE
 * Exact matches are sorted to the top; soft-deleted students are excluded.
 */
export async function searchStudentsByIdentifier(
  query: string,
  limit = 10,
  client: DbClient = defaultDbClient
): Promise<StudentSearchResult[]> {
  const trimmed = query.trim()
  if (!trimmed) return []

  const type = detectIdentifierType(trimmed)
  let dbQuery = client
    .from('students')
    .select('phone, name, email, course, program, attempt, valid_till, status')
    .is('deleted_at', null)
    .limit(limit)

  if (type === 'email') {
    dbQuery = dbQuery.ilike('email', `%${trimmed}%`)
  } else if (type === 'phone') {
    const digits = normalizePhoneDigits(trimmed)
    dbQuery = dbQuery.ilike('phone', `%${digits}%`)
  } else {
    dbQuery = dbQuery.ilike('name', `%${trimmed}%`)
  }

  const { data, error } = await dbQuery
  if (error) throw error
  const rows = (data ?? []) as StudentSearchResult[]

  const normalizedQuery = type === 'phone' ? normalizePhoneDigits(trimmed) : trimmed.toLowerCase()
  return rows.sort((a, b) => {
    const aValue = (type === 'email' ? a.email : type === 'phone' ? normalizePhoneDigits(a.phone) : a.name) ?? ''
    const bValue = (type === 'email' ? b.email : type === 'phone' ? normalizePhoneDigits(b.phone) : b.name) ?? ''
    const aExact = aValue.toLowerCase() === normalizedQuery
    const bExact = bValue.toLowerCase() === normalizedQuery
    if (aExact && !bExact) return -1
    if (!aExact && bExact) return 1
    return (a.name ?? '').localeCompare(b.name ?? '')
  })
}
